/**
 * usePricingUpdates Hook - Phase 2: Pricing Update Management
 * Handles API calls for updating product pricing with optimistic updates
 */

import { useState, useCallback } from 'react';
import { api } from '../api/client';
import { toast } from 'sonner';

export const usePricingUpdates = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  // Update pricing for a single product
  const updateProductPricing = useCallback(async (sku, updates, options = {}) => {
    const { 
      tenantId = 'tenant-123', 
      updatedBy = 'ui_user',
      optimisticUpdate = true,
      onOptimisticUpdate = null
    } = options;

    setIsUpdating(true);
    setError(null);

    try {
      // Optimistic update - update UI immediately
      if (optimisticUpdate && onOptimisticUpdate) {
        onOptimisticUpdate(sku, updates);
      }

      // Make API call
      const response = await api.post(
        `/api/product_pricing/${sku}?tenant_id=${tenantId}&updated_by=${updatedBy}`,
        {
          ...updates,
          change_reason: updates.change_reason || 'Updated via Smart Grid inline editing'
        }
      );

      console.log('Pricing update successful:', response.data);
      
      return {
        success: true,
        data: response.data,
        updatedFields: Object.keys(updates)
      };

    } catch (err) {
      console.error('Pricing update failed:', err);
      
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to update pricing';
      
      setError(errorMessage);
      
      // Revert optimistic update on error
      if (optimisticUpdate && onOptimisticUpdate) {
        // This would need to be handled by the calling component
        console.warn('Optimistic update should be reverted due to error');
      }

      throw new Error(errorMessage);

    } finally {
      setIsUpdating(false);
    }
  }, []);

  // Bulk update multiple products
  const bulkUpdatePricing = useCallback(async (skus, updates, options = {}) => {
    const { tenantId = 'tenant-123', updatedBy = 'ui_user' } = options;

    setIsUpdating(true);
    setError(null);

    try {
      const response = await api.post(
        `/api/product_pricing/bulk_update?tenant_id=${tenantId}&updated_by=${updatedBy}`,
        {
          skus,
          updates: {
            ...updates,
            change_reason: updates.change_reason || 'Bulk update via Smart Grid'
          }
        }
      );

      console.log('Bulk pricing update successful:', response.data);
      
      return {
        success: true,
        data: response.data,
        affectedSkus: skus
      };

    } catch (err) {
      console.error('Bulk pricing update failed:', err);
      
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          'Failed to update pricing for multiple products';
      
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setIsUpdating(false);
    }
  }, []);

  // Recalculate pricing (triggers system price recalculation)
  const recalculatePricing = useCallback(async (skus, options = {}) => {
    const { 
      tenantId = 'tenant-123', 
      updatedBy = 'ui_user',
      forceRecalculate = true,
      updateManufacturingCosts = false
    } = options;

    setIsUpdating(true);
    setError(null);

    try {
      const response = await api.post(
        `/api/product_pricing/bulk_calculate?tenant_id=${tenantId}&updated_by=${updatedBy}`,
        {
          skus: skus || null, // null means all products
          force_recalculate: forceRecalculate,
          update_manufacturing_costs: updateManufacturingCosts
        }
      );

      console.log('Pricing recalculation successful:', response.data);
      
      return {
        success: true,
        data: response.data,
        processedSkus: skus || 'all'
      };

    } catch (err) {
      console.error('Pricing recalculation failed:', err);
      
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          'Failed to recalculate pricing';
      
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setIsUpdating(false);
    }
  }, []);

  // Get pricing history for a product
  const getPricingHistory = useCallback(async (sku, options = {}) => {
    const { tenantId = 'tenant-123', limit = 20 } = options;

    try {
      const response = await api.get(
        `/api/product_pricing/${sku}/history?tenant_id=${tenantId}&limit=${limit}`
      );

      return {
        success: true,
        data: response.data
      };

    } catch (err) {
      console.error('Failed to fetch pricing history:', err);
      
      const errorMessage = err.response?.data?.detail || 
                          'Failed to fetch pricing history';
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  // PHASE B: Wholesale margin update handler
  const updateWholesaleMargin = useCallback(async (sku, newMargin, options = {}) => {
    const {
      onOptimisticUpdate = null,
      onRevertUpdate = null
    } = options;

    try {
      // Optimistic update
      if (onOptimisticUpdate) {
        onOptimisticUpdate(sku, { wholesale_margin_percent: newMargin });
      }

      // Call API
      const result = await updateProductPricing(sku, {
        wholesale_margin_percent: parseFloat(newMargin),
        change_reason: `Wholesale margin updated to ${newMargin}% via Smart Grid`
      }, { optimisticUpdate: false }); // We handle optimistic update manually

      // Update grid with calculated values from API response
      if (onOptimisticUpdate && result.success && result.data.pricing) {
        const pricing = result.data.pricing;
        onOptimisticUpdate(sku, {
          wholesale_margin_percent: pricing.wholesale_margin_percent,
          wholesale_price: pricing.wholesale_price,
          wholesale_net_margin: pricing.wholesale_net_margin,
          price_difference: pricing.price_difference,
          markup_percent: pricing.markup_percent
        });
      }

      // Show success notification
      toast.success(`Wholesale margin updated to ${newMargin}%`);
      
      return result;

    } catch (error) {
      // Revert optimistic update
      if (onRevertUpdate) {
        onRevertUpdate(sku);
      }
      
      toast.error('Failed to update wholesale margin');
      console.error('Wholesale margin update error:', error);
      throw error;
    }
  }, [updateProductPricing]);

  // PHASE B: Retail margin update handler
  const updateRetailMargin = useCallback(async (sku, newMargin, options = {}) => {
    const {
      onOptimisticUpdate = null,
      onRevertUpdate = null
    } = options;

    try {
      // Optimistic update
      if (onOptimisticUpdate) {
        onOptimisticUpdate(sku, { retail_margin_percent: newMargin });
      }

      // Call API
      const result = await updateProductPricing(sku, {
        retail_margin_percent: parseFloat(newMargin),
        change_reason: `Retail margin updated to ${newMargin}% via Smart Grid`
      }, { optimisticUpdate: false }); // We handle optimistic update manually

      // Update grid with calculated values from API response
      if (onOptimisticUpdate && result.success && result.data.pricing) {
        const pricing = result.data.pricing;
        onOptimisticUpdate(sku, {
          retail_margin_percent: pricing.retail_margin_percent,
          retail_price: pricing.retail_price,
          retail_net_margin: pricing.retail_net_margin,
          price_difference: pricing.price_difference,
          markup_percent: pricing.markup_percent
        });
      }

      // Show success notification
      toast.success(`Retail margin updated to ${newMargin}%`);
      
      return result;

    } catch (error) {
      // Revert optimistic update
      if (onRevertUpdate) {
        onRevertUpdate(sku);
      }
      
      toast.error('Failed to update retail margin');
      console.error('Retail margin update error:', error);
      throw error;
    }
  }, [updateProductPricing]);

  // Validate pricing value based on field type
  const validatePricingValue = useCallback((field, value) => {
    switch (field) {
      case 'target_margin_percent':
        return value >= 0 && value <= 100;
      
      case 'wholesale_margin_percent':
        return value >= 0 && value <= 50;
      
      case 'retail_margin_percent':
        return value >= 0 && value <= 70;
      
      case 'final_price':
      case 'system_price':
      case 'manufacturing_cost':
      case 'freight_cost':
      case 'duties_cost':
      case 'handling_cost':
      case 'overhead_allocation':
      case 'ad_spend_allocation':
      case 'fulfillment_cost':
      case 'wholesale_price':
      case 'retail_price':
        return value >= 0;
      
      default:
        return true;
    }
  }, []);

  return {
    // State
    isUpdating,
    error,
    
    // Actions
    updateProductPricing,
    bulkUpdatePricing,
    recalculatePricing,
    getPricingHistory,
    
    // PHASE B: Wholesale/Retail Actions
    updateWholesaleMargin,
    updateRetailMargin,
    
    // Utilities
    validatePricingValue,
    
    // Clear error
    clearError: () => setError(null)
  };
};